// PATH: src/components/SideNav.jsx
// VERSION: 2.4 — Safe areas iOS + nav scrollable + toggle siempre visible

import React from 'react';
import {
  Box,
  VStack,
  Button,
  IconButton,
  Box as CBox,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import navItems from '../data/navigation.json';

import {
  InfoIcon,
  PhoneIcon,
  CalendarIcon,
  SettingsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import { LuCalendarClock } from 'react-icons/lu';
import {
  FaShareAlt,
  FaDonate,
  FaBook,
  FaChurch,
  FaUsers,
  FaImages,
  FaBookOpen,
  FaQuestionCircle,
  FaCalendarAlt,
  FaExternalLinkSquareAlt,
} from 'react-icons/fa';
import { GiStumpRegrowth } from 'react-icons/gi';
import { BiPhotoAlbum } from 'react-icons/bi';

const iconRegistry = {
  InfoIcon,
  PhoneIcon,
  CalendarIcon,
  SettingsIcon,
  BiPhotoAlbum,
  FaExternalLinkSquareAlt,
  FaShareAlt,
  FaDonate,
  FaBook,
  FaChurch,
  FaUsers,
  FaImages,
  FaBookOpen,
  LuCalendarClock,
  FaQuestionCircle,
  GiStumpRegrowth,
  FaCalendarAlt,
};

function getIcon(name) {
  return iconRegistry[name] || InfoIcon;
}

export default function SideNav({ isCollapsed, toggleCollapse, width, isMobile, onCloseDrawer }) {
  const location = useLocation();
  const bg = useColorModeValue('white', 'brand.800');
  const textColor = useColorModeValue('brand.800', 'white');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const activeBg = useColorModeValue('gray.200', 'brand.700');
  const activeColor = useColorModeValue('brand.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'transparent');

  if (isMobile) {
    return (
      <Box flex="1" overflowY="auto" pb="safe-area-inset-bottom">
        <VStack spacing={1} align="stretch" pt={3}>
          {navItems.map((item, idx) => {
            const Icon = getIcon(item.icon);
            const active = location.pathname === item.route;
            return (
              <Link key={idx} to={item.route} onClick={onCloseDrawer}>
                <Button
                  variant="ghost"
                  bg={active ? activeBg : 'transparent'}
                  _hover={{ bg: active ? activeBg : hoverBg }}
                  color={active ? activeColor : textColor}
                  justifyContent="flex-start"
                  leftIcon={<Icon size="18px" />}
                  iconSpacing="3"
                  w="calc(100% - 16px)"
                  mx="8px"
                  mb={1}
                  px={6}
                  py={6}
                  borderRadius="xl"
                >
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      display={{ base: 'none', md: 'flex' }}
      w={`${width}px`}
      minW={`${width}px`}
      bg={bg}
      borderRight="1px solid"
      borderColor={borderColor}
      color={textColor}
      h="100vh"
      pos="fixed"
      inset="0 auto 0 0"
      zIndex="1100"
      flexDir="column"
      sx={{
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      {/* Toggle colapsar/expandir */}
      <Box
        w="100%"
        textAlign="center"
        py={4}
      >
        <IconButton
          icon={isCollapsed ? <ChevronRightIcon boxSize={6} /> : <ChevronLeftIcon boxSize={6} />}
          onClick={toggleCollapse}
          variant="ghost"
          colorScheme="brand"
          color={textColor}
          aria-label="Toggle SideNav"
          size="lg"
          w="40px"
          h="40px"
          minW="40px"
        />
      </Box>

      {/* Navegación scrollable */}
      <Box flex="1" overflowY="auto">
        <VStack
          spacing={1}
          align={isCollapsed ? 'center' : 'stretch'}
          pt={3}
        >
          {navItems.map((item, idx) => {
            const Icon = getIcon(item.icon);
            const active = location.pathname === item.route;
            return (
              <Link key={idx} to={item.route}>
                <Button
                  variant="ghost"
                  bg={active ? activeBg : 'transparent'}
                  _hover={{
                    bg: active ? activeBg : hoverBg,
                  }}
                  color={active ? activeColor : textColor}
                  justifyContent={
                    isCollapsed ? 'center' : 'flex-start'
                  }
                  leftIcon={
                    isCollapsed ? null : <Icon size="18px" />
                  }
                  iconSpacing={isCollapsed ? '0' : '3'}
                  w="calc(100% - 16px)"
                  mx="8px"
                  mb={1}
                  borderRadius="xl"
                >
                  {isCollapsed ? (
                    <Icon size="18px" />
                  ) : (
                    item.label
                  )}
                </Button>
              </Link>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
}
